import type { ItemFieldEntry } from '@cheqroom/api';
import { FC, PropsWithChildren } from 'react';

import { Barcode, QRCode } from '../Info/components';
import {
	Field,
	type FieldOverrideProps,
	Fields,
	InfoFields,
	orderFieldsByDefs,
	resolveCustomFields,
} from '../Info/fields';
import type { FieldRenderOptions, InfoField } from '../Info/fields/Fields.types';
import type { StackProps } from '../Stack';
import { FieldRenderer, Flag, ItemType, Location, PartOfKit, Status } from './components';
import { resolveSystemFieldsFromItem } from './components/Fields/utils';
import { ItemInfoItem, ItemInfoItemProvider } from './ItemInfo.context';
import type { ItemInfoFieldId } from './ItemInfo.types';

interface ComponentComposition {
	Status: typeof Status;
	Flag: typeof Flag;
	PartOfKit: typeof PartOfKit;
	Location: typeof Location;
	ItemType: typeof ItemType;
	Field: FC<FieldOverrideProps<ItemInfoFieldId>>;
	Fields: typeof Fields;
	QRCode: typeof QRCode;
	Barcode: typeof Barcode;
}

export interface Props extends StackProps {
	item?: ItemInfoItem;
	fieldDefs?: ItemFieldEntry[];
	extraFields?: InfoField[];
	isLoading?: boolean;
}

const renderItemField = (field: InfoField, options: FieldRenderOptions) => <FieldRenderer {...options} field={field} />;

const ItemInfo: FC<PropsWithChildren<Props>> & ComponentComposition = ({
	item = {} as ItemInfoItem,
	fieldDefs,
	children,
	...rest
}) => {
	const fields = orderFieldsByDefs(
		[...resolveSystemFieldsFromItem(item, fieldDefs), ...resolveCustomFields(item.fields ?? {}, fieldDefs)],
		fieldDefs
	);

	return (
		<ItemInfoItemProvider item={item} fieldDefs={fieldDefs}>
			<InfoFields {...rest} fieldDefs={fieldDefs} fields={fields} renderField={renderItemField}>
				{children}
			</InfoFields>
		</ItemInfoItemProvider>
	);
};

ItemInfo.Status = Status;
ItemInfo.Flag = Flag;
ItemInfo.PartOfKit = PartOfKit;
ItemInfo.Location = Location;
ItemInfo.ItemType = ItemType;
ItemInfo.Field = Field;
ItemInfo.Fields = Fields;
ItemInfo.QRCode = QRCode;
ItemInfo.Barcode = Barcode;

export { ItemInfo };
